import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { contestMyTimesheetApi, signManyMyTimesheetsApi, signMyTimesheetApi } from "../api/timekeepingApi";
import { COLABORADOR_ESPELHO } from "../routes";

import "../pages/Ponto.css";

type YearMonth = { year: number; month: number };

type Props = {
  onClose: () => void;
  onDone: () => void;
} & (
  | { mode: "ASSINAR" | "CONTESTAR"; year: number; month: number }
  | { mode: "ASSINAR_VARIOS"; months: YearMonth[] }
);

const monthLabel = ({ year, month }: YearMonth) => new Date(year, month - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

/**
 * Assinar (com a senha do portal redigitada) ou contestar o espelho fechado.
 * Assinatura eletrônica simples: o servidor grava data, hora, IP, aparelho e o
 * código (hash) da versão do espelho. A contestação vai para o DP e não trava nada.
 * `ASSINAR_VARIOS` assina os pendentes com uma senha só; o mês que não puder ser
 * assinado volta com o motivo e não impede os outros.
 */
export default function SignTimesheetSheet(props: Props) {
  const { mode, onClose, onDone } = props;
  const [password, setPassword] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const monthName = mode === "ASSINAR_VARIOS" ? "" : monthLabel(props);
  const signing = mode !== "CONTESTAR";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signing && !password) return toast.error("Digite a sua senha.");
    if (mode === "CONTESTAR" && text.trim().length < 10) return toast.error("Explique o que está errado (mínimo de 10 caracteres).");
    setSending(true);
    try {
      if (props.mode === "ASSINAR_VARIOS") {
        const { signed, skipped } = await signManyMyTimesheetsApi(props.months, password);
        if (signed.length) toast.success(signed.length === 1 ? "1 espelho assinado." : `${signed.length} espelhos assinados.`);
        for (const s of skipped) toast.warn(`${monthLabel(s)}: ${s.reason}`);
      } else if (props.mode === "ASSINAR") await signMyTimesheetApi(props.year, props.month, password);
      else await contestMyTimesheetApi(props.year, props.month, text.trim());
      onDone();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível enviar.");
      if (signing) setPassword("");
    } finally {
      setSending(false);
    }
  };

  const passwordField = (
    <div className="colabField" style={{ marginBottom: 16 }}>
      <label htmlFor="signPassword">Sua senha do portal</label>
      <input
        id="signPassword"
        type="password"
        className="colabInput"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
      />
    </div>
  );

  return (
    <div className="colabSheet__overlay">
      <form className="colabSheet" role="dialog" aria-modal="true" aria-labelledby="signSheetTitle" onSubmit={submit}>
        <div className="colabSheet__head">
          <h2 id="signSheetTitle" className="colabSheet__title">
            {mode === "ASSINAR" ? "Assinar o espelho" : mode === "ASSINAR_VARIOS" ? "Assinar os espelhos" : "Contestar o espelho"}
          </h2>
          <button type="button" className="colabSheet__close" aria-label="Fechar" onClick={onClose} disabled={sending}>
            ×
          </button>
        </div>

        {props.mode === "ASSINAR_VARIOS" ? (
          <>
            <p className="colabNotice colabNotice--warn">Confira cada espelho antes de assinar. Se algum estiver errado, abra-o e conteste em vez de assinar.</p>
            <ul className="colabSignList">
              {props.months.map((m) => (
                <li key={`${m.year}-${m.month}`}>
                  <span>{monthLabel(m)}</span>
                  <Link to={`${COLABORADOR_ESPELHO()}?ano=${m.year}&mes=${m.month}`}>Conferir</Link>
                </li>
              ))}
            </ul>
            <p className="colabHint">
              Ao assinar, você confirma que as marcações, as horas e as faltas desses {props.months.length} espelhos estão corretas. Ficam registrados a data, a hora, o
              endereço de internet (IP) e o aparelho usado.
            </p>
            {passwordField}
            <button type="submit" className="colabBtn colabBtn--primary" disabled={sending}>
              {sending ? "Assinando..." : `Concordo e assino os ${props.months.length}`}
            </button>
          </>
        ) : mode === "ASSINAR" ? (
          <>
            <p className="colabNotice">
              Ao assinar, você confirma que as marcações, as horas e as faltas do espelho de <strong>{monthName}</strong> estão corretas.
            </p>
            <p className="colabHint">Ficam registrados a data, a hora, o endereço de internet (IP) e o aparelho usado.</p>
            {passwordField}
            <button type="submit" className="colabBtn colabBtn--primary" disabled={sending}>
              {sending ? "Assinando..." : "Concordo e assino"}
            </button>
          </>
        ) : (
          <>
            <p className="colabNotice">
              Conte ao DP o que está errado no espelho de <strong>{monthName}</strong>. O DP confere, corrige se for o caso e você assina a nova versão.
            </p>
            <div className="colabField" style={{ marginBottom: 16 }}>
              <label htmlFor="contestText">O que está errado? (obrigatório)</label>
              <textarea id="contestText" className="colabTextarea" maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
            </div>
            <button type="submit" className="colabBtn colabBtn--primary" disabled={sending}>
              {sending ? "Enviando..." : "Enviar contestação"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
